import { useCallback, useEffect, useRef, useState } from 'react';
import { getAgiBackTaskById, type AgiBackTaskInfo } from '../../api/agiBackTask';
import {
  extractTaskDetailMaterialRefs,
  resolveTaskDetailMaterialDisplays,
  type TaskDetailMaterialDisplay,
} from '../../utils/taskDetailMaterialResolve';
import {
  getLocalStorageValue,
  SEEDANCE_STORAGE_KEYS,
} from '../../utils/seedanceStorage';
import { extractApiMessage, isAbortLike } from '../../utils/seedanceRequestUtils';

type TaskDetailDebugFn = (msg: string, isError?: boolean) => void;

export function useTaskDetail(taskId: string | null, onDebug?: TaskDetailDebugFn) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<AgiBackTaskInfo | null>(null);
  const [materials, setMaterials] = useState<TaskDetailMaterialDisplay[]>([]);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const loadSeqRef = useRef(0);
  const inflightRef = useRef<Map<string, Promise<void>>>(new Map());
  const onDebugRef = useRef(onDebug);
  onDebugRef.current = onDebug;

  const load = useCallback(async (id: string, debug?: TaskDetailDebugFn) => {
    const trimmed = id.trim();
    if (!trimmed) return;

    const inflight = inflightRef.current.get(trimmed);
    if (inflight) return inflight;

    const seq = ++loadSeqRef.current;
    const log = debug ?? onDebugRef.current;

    const promise = (async () => {
      setLoading(true);
      setError(null);
      if (seq === loadSeqRef.current) {
        setDetail(null);
        setMaterials([]);
        setMaterialsLoading(false);
      }

      try {
        const data = await getAgiBackTaskById(trimmed, {
          onDebug: log,
          systemToken: getLocalStorageValue(SEEDANCE_STORAGE_KEYS.apiKey),
          userId: getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId),
        });
        if (seq !== loadSeqRef.current) return;
        setDetail(data);
        log?.(`GET /proxy/agiBack/v1/tasks/${trimmed} 成功`);

        const refs = extractTaskDetailMaterialRefs(data);
        if (refs.length) {
          setMaterialsLoading(true);
          const items = await resolveTaskDetailMaterialDisplays(refs, { onDebug: log });
          if (seq !== loadSeqRef.current) return;
          setMaterials(items);
        }
      } catch (e: unknown) {
        if (seq !== loadSeqRef.current) return;
        if (isAbortLike(e)) return;
        const errMsg =
          extractApiMessage(e) || (e instanceof Error ? e.message : '') || '加载任务详情失败';
        setError(errMsg);
        log?.(`任务详情失败: ${errMsg}`, true);
      } finally {
        if (seq === loadSeqRef.current) {
          setLoading(false);
          setMaterialsLoading(false);
        }
      }
    })().finally(() => {
      inflightRef.current.delete(trimmed);
    });

    inflightRef.current.set(trimmed, promise);
    return promise;
  }, []);

  useEffect(() => {
    if (!taskId) return;
    void load(taskId);
  }, [taskId, load]);

  return {
    loading,
    error,
    detail,
    materials,
    materialsLoading,
    load,
  };
}
